import { useCallback } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useQueryClient } from '@tanstack/react-query'
import { usePlanTableStore } from '@editorStores'
import { useSelectionMode } from '@editorHooks'
import { getPlanTableTemplates } from '@queries'
import { usePersistentParams } from 'src/hooks'

export const ADD_PLAN_TABLES_SELECTION_KEY = 'add-plan-tables'

const getPlacementMessage = (templateKey: string | null): string => {
  if (!templateKey) {
    return 'Untere linke Ecke der Tabelle anwählen'
  }
  return 'Untere linke Ecke der Vorlagentabelle anwählen'
}

/**
 * Toggles plan-table placement mode. Point pick and table creation are handled
 * by `PlanTableController` in the scene.
 */
const usePlanTableTool = (): {
  isActive: boolean
  toggle: () => void
  startPlacement: (templateKey: string | null) => void
} => {
  const setIsVisible = usePlanTableStore(state => state.setIsVisible)
  const setIsActive = usePlanTableStore(state => state.setIsActive)
  const setPendingTemplateKey = usePlanTableStore(state => state.setPendingTemplateKey)
  const { selectionModeKey, setSelectionMode, unsetSelectionMode } = useSelectionMode()
  const queryClient = useQueryClient()
  const { projectId }: { projectId?: string } = usePersistentParams()

  const isActive = selectionModeKey === ADD_PLAN_TABLES_SELECTION_KEY

  const startPlacement = useCallback(
    (templateKey: string | null) => {
      setPendingTemplateKey(templateKey)
      setIsActive(true)
      setIsVisible(true)
      setSelectionMode({
        key: ADD_PLAN_TABLES_SELECTION_KEY,
        message: getPlacementMessage(templateKey),
      })
    },
    [setIsActive, setIsVisible, setPendingTemplateKey, setSelectionMode],
  )

  const toggle = useCallback(() => {
    if (isActive) {
      setPendingTemplateKey(null)
      unsetSelectionMode()
      return
    }
    startPlacement(null)
  }, [isActive, setPendingTemplateKey, startPlacement, unsetSelectionMode])

  useHotkeys(
    ['shift+t'],
    () => {
      if (isActive) {
        toggle()
        return
      }
      void queryClient
        .fetchQuery({
          queryKey: getPlanTableTemplates.getKey(projectId),
          queryFn: () => getPlanTableTemplates.request(projectId),
        })
        .catch(() => undefined)
      startPlacement(null)
    },
    [isActive, projectId, queryClient, startPlacement, toggle],
  )

  return { isActive, toggle, startPlacement }
}

export default usePlanTableTool
